import { Routes } from '@angular/router';
import { redirectUnauthorizedTo } from './guards/auth.guard';
import { requireOrg } from './guards/permission.guard';
import {
  REQUIRES_PRODUCT,
  requireProjectOrganization,
  requireProduct,
  requireProductChild,
} from './guards/product.guard';

export const routes: Routes = [
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    path: '__/auth',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./pages/auth-redirect/auth-redirect.component').then((m) => m.AuthRedirectComponent),
  },
  {
    // Data residency: the active org's region can't be reached (requireOrg).
    path: 'region-unavailable',
    loadComponent: () =>
      import('./pages/region-unavailable/region-unavailable.component').then(
        (m) => m.RegionUnavailableComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    // Login is centralized in the auth portal. This route only needs to resolve
    // so code that navigates to `/login` (e.g. sign-out) hands off to the portal:
    // the guard calls bridge.login() for unauthenticated users before the (empty)
    // component renders.
    path: 'login',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    // Full-screen storyboard workspace — no app sidebar (its own chrome), like Build's editor.
    path: 'storyboards/:id',
    loadComponent: () =>
      import('./pages/compose/compose-workspace.component').then(
        (m) => m.ComposeWorkspaceComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg(), requireProduct('create')],
    data: { [REQUIRES_PRODUCT]: 'create' },
  },
  {
    // Full-screen walkthrough workspace — live browser view + step log, no app sidebar.
    path: 'walkthroughs/:id',
    loadComponent: () =>
      import('./pages/walkthrough/walkthrough-workspace.component').then(
        (m) => m.WalkthroughWorkspaceComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg(), requireProduct('create')],
    data: { [REQUIRES_PRODUCT]: 'create' },
  },
  {
    // Full-screen asset workspace — agent chat + version filmstrip, no app sidebar.
    path: 'create/workspace/:projectId/:lineageId',
    loadComponent: () =>
      import('./pages/create/asset-workspace/asset-workspace.component').then(
        (m) => m.AssetWorkspaceComponent,
      ),
    canActivate: [
      redirectUnauthorizedTo('/login'),
      requireOrg(),
      requireProjectOrganization(),
      requireProduct('create'),
    ],
    data: { [REQUIRES_PRODUCT]: 'create' },
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
    // C8: one layout-level guard re-checks entitlement on EVERY child
    // navigation for children marked with `data.requiresProduct`, replacing
    // per-route `requireProduct` repetition.
    canActivateChild: [requireProductChild()],
    children: [
      {
        path: 'new',
        loadComponent: () =>
          import('./pages/new-user/new-user.component').then((m) => m.NewUserComponent),
      },
      {
        // Home is Create's primary data surface, so it must be guarded just like
        // every creation workspace. `/product-disabled` remains the neutral denial page.
        path: 'home',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        canActivate: [requireOrg()],
        data: { [REQUIRES_PRODUCT]: 'create' },
      },
      {
        // Inside a folder. Same component as the root (one level of nesting), and
        // product-guarded exactly as the old `projects/:id` page was.
        path: 'home/:folderId',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        canActivate: [requireOrg()],
        data: { [REQUIRES_PRODUCT]: 'create' },
      },
      {
        // Not product-guarded: this is the denial screen itself.
        path: 'product-disabled',
        loadComponent: () =>
          import('./pages/product-disabled/product-disabled.component').then(
            (m) => m.ProductDisabledComponent,
          ),
      },
      // Projects merged into Home. Kept as redirects so existing deep links,
      // bookmarks and in-app references keep resolving.
      { path: 'projects', redirectTo: 'home', pathMatch: 'full' },
      { path: 'projects/:id', redirectTo: 'home/:id', pathMatch: 'full' },
      {
        path: 'create/image',
        loadComponent: () =>
          import('./pages/create/create.component').then((m) => m.CreateComponent),
        data: { type: 'image', [REQUIRES_PRODUCT]: 'create' },
        canActivate: [requireOrg()],
      },
      {
        path: 'create/video',
        loadComponent: () =>
          import('./pages/create/create.component').then((m) => m.CreateComponent),
        data: { type: 'video', [REQUIRES_PRODUCT]: 'create' },
        canActivate: [requireOrg()],
      },
      {
        path: 'create/narration',
        loadComponent: () =>
          import('./pages/create/create.component').then((m) => m.CreateComponent),
        data: { type: 'audio', audioMode: 'narration', [REQUIRES_PRODUCT]: 'create' },
        canActivate: [requireOrg()],
      },
      {
        path: 'create/music',
        loadComponent: () =>
          import('./pages/create/create.component').then((m) => m.CreateComponent),
        data: { type: 'audio', audioMode: 'music', [REQUIRES_PRODUCT]: 'create' },
        canActivate: [requireOrg()],
      },
      { path: 'create/audio', redirectTo: 'create/narration', pathMatch: 'full' },
      {
        path: 'create',
        redirectTo: 'create/image',
        pathMatch: 'full',
      },
      {
        path: 'storyboards',
        loadComponent: () =>
          import('./pages/compose/compose.component').then((m) => m.ComposeComponent),
        canActivate: [requireOrg()],
        data: { [REQUIRES_PRODUCT]: 'create' },
      },
      {
        path: 'walkthroughs',
        loadComponent: () =>
          import('./pages/walkthrough/walkthrough-list.component').then(
            (m) => m.WalkthroughListComponent,
          ),
        canActivate: [requireOrg()],
        data: { [REQUIRES_PRODUCT]: 'create' },
      },
      {
        path: 'account',
        loadComponent: () =>
          import('./pages/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'settings',
        redirectTo: 'account',
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
  // Unknown URLs land on Home instead of a blank NG04002 page. The auth guard
  // on the layout route still handles the logged-out case.
  { path: '**', redirectTo: 'home' },
];
